import { IconButton, Stack, Tooltip, Typography, Box, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import { HMIScreenKey, HMIScreenMeta } from '../../types';

export interface ScreenTabStripProps {
  options: HMIScreenMeta[];
  activeKey: HMIScreenKey;
  /** `true` for the top-level column - shows the trailing "Add tab" button and a plain label. */
  isRoot: boolean;
  readOnly?: boolean;
  onSelect: (key: HMIScreenKey) => void;
  onMove: (key: HMIScreenKey, direction: -1 | 1) => void;
  onRename: (key: HMIScreenKey, label: string) => void;
  onDelete: (key: HMIScreenKey) => void;
  /** Adds a subtab nested under the given tab - available on every tab, at every depth. */
  onAddSubtab: (parentKey: HMIScreenKey) => void;
  /** Adds a new sibling tab at this column's level (root tabs, or subtabs of whatever this column's parent is). */
  onAddSibling: () => void;
}

/**
 * One vertical column of tabs in the layout builder's tab/subtab tree. The
 * same component renders the top-level tab column and every nested subtab
 * column (rendered side-by-side by the caller) - each tab gets the same
 * move/rename/delete controls plus an "Add subtab" icon, so subtabs can be
 * added to any tab at any depth.
 */
function ScreenTabStrip({
  options,
  activeKey,
  isRoot,
  readOnly,
  onSelect,
  onMove,
  onRename,
  onDelete,
  onAddSubtab,
  onAddSibling,
}: ScreenTabStripProps) {
  return (
    <Stack
      direction="column"
      spacing={0.5}
      sx={{
        minWidth: isRoot ? 180 : 160,
        maxHeight: 360,
        overflowY: 'auto',
        pr: 1,
        pl: isRoot ? 0 : 1,
        borderLeft: isRoot ? 'none' : '2px solid',
        borderColor: 'divider',
      }}
    >
      {options.map((s, idx) => {
        const active = s.key === activeKey;
        const activeBgcolor = isRoot ? 'primary.main' : 'primary.light';
        return (
          <Box
            key={s.key}
            onClick={() => onSelect(s.key)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.25,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              bgcolor: active ? activeBgcolor : 'action.hover',
              color: active ? 'primary.contrastText' : 'text.primary',
            }}
          >
            <Typography variant="body2" sx={{ px: 0.5, flexGrow: 1, minWidth: 0 }} noWrap>
              {s.label}
            </Typography>
            {!readOnly && (
              <>
                <Tooltip title="Move earlier">
                  <span>
                    <IconButton
                      size="small"
                      disabled={idx === 0}
                      sx={{ color: 'inherit' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onMove(s.key, -1);
                      }}
                    >
                      <ArrowUpwardIcon fontSize="inherit" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move later">
                  <span>
                    <IconButton
                      size="small"
                      disabled={idx === options.length - 1}
                      sx={{ color: 'inherit' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onMove(s.key, 1);
                      }}
                    >
                      <ArrowDownwardIcon fontSize="inherit" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Add subtab">
                  <IconButton
                    size="small"
                    sx={{ color: 'inherit' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddSubtab(s.key);
                    }}
                  >
                    <PlaylistAddIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Rename tab">
                  <IconButton
                    size="small"
                    sx={{ color: 'inherit' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRename(s.key, s.label);
                    }}
                  >
                    <DriveFileRenameOutlineIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete tab">
                  <IconButton
                    size="small"
                    sx={{ color: 'inherit' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(s.key);
                    }}
                  >
                    <DeleteIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        );
      })}
      {!readOnly && isRoot && (
        <Button size="small" startIcon={<AddIcon />} onClick={onAddSibling}>
          Add tab
        </Button>
      )}
    </Stack>
  );
}

export default ScreenTabStrip;
